<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { navigationItems } from '../../data/navigation'

const activeId = ref('')

/*
  Scroll spy. Uses IntersectionObserver rather than a scroll handler doing
  getBoundingClientRect on every section — same result, no per-frame layout.
*/
let observer: IntersectionObserver | null = null

onMounted(() => {
  const targets = navigationItems
    .map((item) => document.getElementById(item.href.slice(1)))
    .filter((el): el is HTMLElement => !!el)

  observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
      if (visible) activeId.value = visible.target.id
    },
    { rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.25, 0.5, 1] }
  )

  targets.forEach((el) => observer?.observe(el))
})

onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
  <!--
    Vertical section rail, fixed to the right edge — an index down the margin
    rather than a menu across the top.
  -->
  <nav class="rail" role="navigation" aria-label="Sections">
    <a
      v-for="(item, i) in navigationItems"
      :key="item.routeName"
      :href="item.href"
      class="stop"
      :class="{ 'is-active': activeId === item.href.slice(1) }"
      :aria-current="activeId === item.href.slice(1) ? 'true' : undefined"
    >
      <span class="idx">{{ String(i + 1).padStart(2, '0') }}</span>
      <span class="lbl">{{ item.name }}</span>
      <span class="tick" aria-hidden="true"></span>
    </a>
  </nav>
</template>

<style scoped>
.rail {
  position: fixed;
  right: clamp(0.6rem, 1.4vw, 1.5rem);
  top: 50%;
  translate: 0 -50%;
  z-index: 55;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.7rem;
}

.stop {
  writing-mode: vertical-rl;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-mono);
  font-size: 0.6rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--c-ink-3);
  transition: color 0.25s ease;
}
.idx {
  font-size: 0.55rem;
  color: var(--c-ink-3);
  transition: color 0.25s ease;
}
/* grows downward alongside the active label */
.tick {
  width: 1px;
  height: 0;
  background: var(--c-accent);
  transition: height 0.35s var(--ease-out-quint);
}

.stop:hover {
  color: var(--c-ink);
}
.stop.is-active {
  color: var(--c-ink);
}
.stop.is-active .idx {
  color: var(--c-accent);
}
.stop.is-active .tick {
  height: 1.4rem;
}

@media (max-width: 899px) {
  .rail {
    display: none;
  }
}
</style>
